import type { ReactNode } from "react";

import type { ReplacementAgePayload, ReplacementAgeRow } from "@busright/data-contract";
import { COLORS, RAG } from "@busright/ui";

import { formatInt, formatPct } from "@/lib/format";

// R5 — replacement-age (replacement-duration proxy) panel. Device age is a FACT (provisioned_on is
// 100% populated); "how long was the REPLACED unit in service" is an INFERENCE because E2 orders
// carry no serial — a retired (Esper Disabled) unit's age stands in for the outgoing unit's tenure.
// `lifecycleClass` separates the proxy ("retired") from standing-fleet context ("active"); each row
// carries its own confidence so the honesty is visible per-row, not just in a caveat paragraph.

const DAYS_PER_YEAR = 365.25;
const ageYears = (days: number | null): string =>
  days === null ? "—" : (days / DAYS_PER_YEAR).toFixed(1);

const CLASS_LABEL: Record<ReplacementAgeRow["lifecycleClass"], string> = {
  retired: "Retired (replacement-age proxy)",
  active: "Active (standing fleet)",
};

// Confidence renders as a plain percentage — never a RAG color (confidence is a data-quality fact,
// not a health score).
const CONFIDENCE_PCT_SCALE = 100;
// Fallback confidence for the retired-proxy class when no row is present (matches the service's
// documented retired-row confidence in replacement-age.ts).
const RETIRED_FALLBACK_CONFIDENCE = 0.6;
const confidenceLabel = (c: number): string =>
  `${Math.round(c * CONFIDENCE_PCT_SCALE)}% confidence`;

// Font weight for the highlighted "retired" row class vs the muted "active" context rows.
const RETIRED_WEIGHT = 600;
const ACTIVE_WEIGHT = 400;

export interface LcReplacementAgeProps {
  readonly payload: ReplacementAgePayload;
}

export const LcReplacementAge = ({ payload }: LcReplacementAgeProps): ReactNode => {
  const retiredRows = payload.rows.filter((r) => r.lifecycleClass === "retired");
  const activeRows = payload.rows.filter((r) => r.lifecycleClass === "active");
  const rows = [...retiredRows, ...activeRows];

  return (
    <div>
      <section
        className="metric-strip"
        style={{ marginBottom: 12 }}
        aria-label="Replacement-age headline"
      >
        <div className="metric-chip">
          <span className="mc-label">Retired units (proxy fleet)</span>
          <span className="mc-value br-tabular-nums">{formatInt(payload.retiredDeviceCount)}</span>
        </div>
        <div className="metric-chip">
          <span className="mc-label">Avg age at retirement</span>
          <span className="mc-value br-tabular-nums">{ageYears(payload.retiredAvgAgeDays)}y</span>
        </div>
        <div className="metric-chip">
          <span className="mc-label">Confidence (retired proxy)</span>
          <span className="mc-value br-tabular-nums" style={{ color: COLORS.textSecondary }}>
            {retiredRows.length > 0
              ? confidenceLabel(retiredRows[0]?.confidence ?? RETIRED_FALLBACK_CONFIDENCE)
              : "—"}
          </span>
        </div>
      </section>

      <div style={{ overflowX: "auto" }}>
        <table className="data-table" style={{ minWidth: 560 }}>
          <thead>
            <tr>
              <th>Class</th>
              <th>Model</th>
              <th>Age cohort</th>
              <th className="num">Devices</th>
              <th className="num">Avg age</th>
              <th className="num">Median age</th>
              <th className="num">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.lifecycleClass}-${r.hwModel}-${r.ageCohort}`}>
                <td>
                  <span
                    style={{
                      color: r.lifecycleClass === "retired" ? RAG.WARN : COLORS.textSecondary,
                      fontWeight: r.lifecycleClass === "retired" ? RETIRED_WEIGHT : ACTIVE_WEIGHT,
                    }}
                  >
                    {CLASS_LABEL[r.lifecycleClass]}
                  </span>
                </td>
                <td>{r.hwModel}</td>
                <td>{r.ageCohort}</td>
                <td className="num br-tabular-nums">{formatInt(r.deviceCount)}</td>
                <td className="num br-tabular-nums">{ageYears(r.avgAgeDays)}y</td>
                <td className="num br-tabular-nums">{ageYears(r.medianAgeDays)}y</td>
                <td className="num br-tabular-nums" style={{ color: COLORS.textMuted }}>
                  {formatPct(r.confidence * CONFIDENCE_PCT_SCALE, 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 10, fontSize: 11, color: "var(--br-text-muted)" }}>
        {payload.inferenceCaveat}
      </p>
    </div>
  );
};
